/**
 * Lift2Fit — the session runtime's sound design.
 *
 * Six short tones, synthesised with the Web Audio API so the product ships no
 * audio assets: no licensing, no bundle weight, and the same "voice" on every
 * platform that can host the state machine.
 *
 *   countdownStart · a note to put the phone down
 *   tick           · one per countdown numeral (5 → 1)
 *   go             · a distinct rising triad — start the set
 *   restStart      · a soft two-note drop into rest
 *   runIn          · the heads-up: the next set is seconds away
 *   complete       · the session's closing chime
 *
 * AUDIO RULES (this is what makes sound actually audible):
 *  - Browsers only allow audio after a gesture, so the context is created and
 *    `resume()`d on EVERY user gesture (a module-level pointerdown/keydown
 *    listener), not once: a context that the browser suspended while the tab was
 *    backgrounded is resumed by the next tap.
 *  - The context is resumed explicitly; a tone asked for while it is suspended is
 *    held and played the moment `resume()` resolves — never scheduled into a
 *    dead context and dropped.
 *  - Every tone runs through ONE master gain that is never 0, so the whole
 *    runtime has a single, audible level (and the checks can assert it).
 *  - A muted runtime never touches the AudioContext at all; `setRuntimeMuted`
 *    resumes it when the lifter unmutes.
 */

export type RuntimeTone = 'countdownStart' | 'tick' | 'go' | 'restStart' | 'runIn' | 'complete';

interface ToneSpec {
  /** Hz — the base pitch. */
  frequency: number;
  /** Where the pitch lands, for a glide (optional). */
  sweepTo?: number;
  /** Length of one pulse, in milliseconds. */
  durationMs: number;
  /** Pulses, played back to back — an arpeggio without extra code. */
  pulses: number;
  /** Silence between pulses, in milliseconds. */
  gapMs: number;
  /** Peak gain; deliberately low so a phone beside a barbell is not jarring. */
  gain: number;
  wave: OscillatorType;
}

/** Distinct by pitch, wave and shape — never two events share a spec. */
const TONES: Record<RuntimeTone, ToneSpec> = {
  countdownStart: { frequency: 392, sweepTo: 523.25, durationMs: 180, pulses: 1, gapMs: 0, gain: 0.16, wave: 'sine' },
  tick: { frequency: 659.25, durationMs: 70, pulses: 1, gapMs: 0, gain: 0.12, wave: 'square' },
  go: { frequency: 523.25, durationMs: 110, pulses: 3, gapMs: 40, gain: 0.2, wave: 'triangle' },
  restStart: { frequency: 440, sweepTo: 329.63, durationMs: 260, pulses: 1, gapMs: 0, gain: 0.14, wave: 'sine' },
  runIn: { frequency: 880, durationMs: 90, pulses: 2, gapMs: 90, gain: 0.18, wave: 'sawtooth' },
  complete: { frequency: 587.33, durationMs: 160, pulses: 3, gapMs: 50, gain: 0.16, wave: 'triangle' },
};

const MUTE_KEY = 'l2f.runtime.muted';

/** The one master level. Never 0: a muted runtime does not build a graph at all. */
const MASTER_GAIN = 0.9;

/** A gesture asked for a tone before the context was running: play it on resume. */
const MAX_HELD_TONES = 8;

let muted = readStoredMute();
let context: AudioContext | null = null;
let master: GainNode | null = null;
let resuming: Promise<void> | null = null;
let held: RuntimeTone[] = [];

function readStoredMute(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isRuntimeMuted(): boolean {
  return muted;
}

export function setRuntimeMuted(value: boolean): void {
  muted = value;
  try {
    window.localStorage.setItem(MUTE_KEY, value ? '1' : '0');
  } catch {
    /* private mode — the in-memory flag still mutes this session */
  }
  if (value) held = [];
  else unlockRuntimeAudio();
}

type AudioContextConstructor = typeof AudioContext;

function audioContextConstructor(): AudioContextConstructor | undefined {
  if (typeof window === 'undefined') return undefined;
  const legacy = window as Window & { webkitAudioContext?: AudioContextConstructor };
  return window.AudioContext ?? legacy.webkitAudioContext;
}

/** The shared master bus, built once per context and never at 0. */
function ensureGraph(audio: AudioContext): GainNode {
  if (master === null) {
    master = audio.createGain();
    master.gain.value = MASTER_GAIN;
    master.connect(audio.destination);
  }
  return master;
}

function resumeContext(audio: AudioContext): Promise<void> {
  if (audio.state === 'running') return Promise.resolve();
  if (resuming === null) {
    resuming = audio
      .resume()
      .catch(() => undefined)
      .then(() => {
        resuming = null;
      });
  }
  return resuming;
}

/**
 * Create or resume the shared context. Safe to call from any gesture handler;
 * does nothing (and swallows the failure) when the browser has no audio.
 */
export function unlockRuntimeAudio(): void {
  if (muted) return;
  try {
    const Ctor = audioContextConstructor();
    if (!Ctor) return;
    context ??= new Ctor();
    ensureGraph(context);
    if (context.state !== 'running') {
      void resumeContext(context).then(() => flushHeld());
    }
  } catch {
    context = null;
    master = null;
  }
}

function schedule(audio: AudioContext, bus: GainNode, tone: RuntimeTone): void {
  const spec = TONES[tone];
  // A hair in the future so the first pulse is never scheduled into the past.
  const start = audio.currentTime + 0.01;
  for (let pulse = 0; pulse < spec.pulses; pulse += 1) {
    const at = start + (pulse * (spec.durationMs + spec.gapMs)) / 1000;
    const endsAt = at + spec.durationMs / 1000;
    const oscillator = audio.createOscillator();
    const amp = audio.createGain();
    oscillator.type = spec.wave;
    oscillator.frequency.setValueAtTime(spec.frequency, at);
    if (spec.sweepTo !== undefined) oscillator.frequency.linearRampToValueAtTime(spec.sweepTo, endsAt);
    amp.gain.setValueAtTime(0.0001, at);
    amp.gain.exponentialRampToValueAtTime(spec.gain, at + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, endsAt);
    oscillator.connect(amp);
    amp.connect(bus);
    oscillator.start(at);
    oscillator.stop(endsAt + 0.02);
  }
}

function flushHeld(): void {
  if (muted) {
    held = [];
    return;
  }
  if (context === null || context.state !== 'running') return;
  const bus = ensureGraph(context);
  const queued = held;
  held = [];
  for (const tone of queued) schedule(context, bus, tone);
}

/** Play one of the six runtime tones. Silent when muted or audio is unavailable. */
export function playRuntimeTone(tone: RuntimeTone): void {
  if (muted) return;
  try {
    const Ctor = audioContextConstructor();
    if (!Ctor) return;
    context ??= new Ctor();
    const audio = context;
    const bus = ensureGraph(audio);
    if (audio.state === 'running') {
      schedule(audio, bus, tone);
      return;
    }
    // Suspended (never unlocked, or backgrounded): resume and play on resolve,
    // rather than scheduling into a context that will not sound.
    held = [...held, tone].slice(-MAX_HELD_TONES);
    void resumeContext(audio).then(() => flushHeld());
  } catch {
    /* audio unavailable — silence is an acceptable failure */
  }
}

/**
 * Keep the context awake for the life of the page: every gesture may be the one
 * that revives a context the browser suspended while the tab was in the
 * background. Registered once, at module load.
 */
if (typeof window !== 'undefined') {
  const unlock = () => unlockRuntimeAudio();
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
  window.addEventListener('focus', unlock);
  window.addEventListener('online', unlock);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') unlockRuntimeAudio();
  });
}
